import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import axios from 'axios';
import '../styles/Stories.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_30e70ec7-9b70-4809-beca-18d857724f80/artifacts/ri9zkzxm_photo_2026-03-03_22-25-58.jpg";

const Stories = () => {
  const [stories, setStories] = useState([]);
  const [activeStoryIndex, setActiveStoryIndex] = useState(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);
  const progressInterval = useRef(null);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stories`);
      setStories(response.data || []);
    } catch (error) {
      console.error('Error fetching stories:', error);
    }
  };

  const openStory = (storyIndex) => {
    setActiveStoryIndex(storyIndex);
    setActiveSlideIndex(0);
    setProgress(0);
    setIsPaused(false);
    const story = stories[storyIndex];
    if (story?.media?.length > 0) {
      startProgress(story.media[0]);
    }
  };

  const closeStory = () => {
    setActiveStoryIndex(null);
    setActiveSlideIndex(0);
    setProgress(0);
    setIsPaused(false);
    clearInterval(progressInterval.current);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Check if mobile
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const startProgress = (mediaItem) => {
    clearInterval(progressInterval.current);
    // Use 2 seconds for images on mobile, otherwise use default (5 seconds)
    const defaultDuration = isMobile ? 2 : 5;
    const duration = (mediaItem?.duration || defaultDuration) * 1000;
    const step = 100 / (duration / 50);

    progressInterval.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          goToNextSlide();
          return 0;
        }
        return prev + step;
      });
    }, 50);
  };

  const goToNextSlide = () => {
    const currentStory = stories[activeStoryIndex];
    if (!currentStory) return;

    const mediaCount = currentStory.media?.length || 0;
    
    if (activeSlideIndex < mediaCount - 1) {
      // Next slide in current story
      const nextSlide = activeSlideIndex + 1;
      setActiveSlideIndex(nextSlide);
      setProgress(0);
      startProgress(currentStory.media[nextSlide]);
    } else if (activeStoryIndex < stories.length - 1) {
      // Next story
      const nextStory = activeStoryIndex + 1;
      setActiveStoryIndex(nextStory);
      setActiveSlideIndex(0);
      setProgress(0);
      if (stories[nextStory]?.media?.length > 0) {
        startProgress(stories[nextStory].media[0]);
      }
    } else {
      // End of all stories
      closeStory();
    }
  };

  const goToPrevSlide = () => {
    if (activeSlideIndex > 0) {
      // Previous slide in current story
      const prevSlide = activeSlideIndex - 1;
      setActiveSlideIndex(prevSlide);
      setProgress(0);
      startProgress(stories[activeStoryIndex].media[prevSlide]);
    } else if (activeStoryIndex > 0) {
      // Previous story (last slide)
      const prevStory = activeStoryIndex - 1;
      const lastSlideIndex = (stories[prevStory]?.media?.length || 1) - 1;
      setActiveStoryIndex(prevStory);
      setActiveSlideIndex(lastSlideIndex);
      setProgress(0);
      if (stories[prevStory]?.media?.length > 0) {
        startProgress(stories[prevStory].media[lastSlideIndex]);
      }
    }
  };

  const togglePause = () => {
    setIsPaused(!isPaused);
    if (isPaused) {
      const currentMedia = stories[activeStoryIndex]?.media?.[activeSlideIndex];
      startProgress(currentMedia);
      if (videoRef.current) videoRef.current.play();
    } else {
      clearInterval(progressInterval.current);
      if (videoRef.current) videoRef.current.pause();
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const getMediaUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  // Fallback placeholder for missing media - creates a nice gradient placeholder
  const FALLBACK_IMAGE = 'data:image/svg+xml,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#1e3a5f"/>
          <stop offset="50%" style="stop-color:#0d1f3c"/>
          <stop offset="100%" style="stop-color:#0a1628"/>
        </linearGradient>
        <radialGradient id="glow" cx="30%" cy="30%" r="50%">
          <stop offset="0%" style="stop-color:rgba(0,255,209,0.2)"/>
          <stop offset="100%" style="stop-color:transparent"/>
        </radialGradient>
      </defs>
      <rect fill="url(#grad)" width="100" height="100"/>
      <rect fill="url(#glow)" width="100" height="100"/>
      <circle cx="50" cy="40" r="15" fill="rgba(0,255,209,0.3)"/>
      <path d="M30 70 L50 55 L70 70 L70 85 L30 85 Z" fill="rgba(0,255,209,0.2)"/>
    </svg>
  `.trim());

  // Get first media item's thumbnail for story preview
  const getStoryThumbnail = (story) => {
    if (story.media && story.media.length > 0 && story.media[0].url) {
      return getMediaUrl(story.media[0].url);
    }
    // Legacy support for old schema
    if (story.media_url) {
      return getMediaUrl(story.media_url);
    }
    return FALLBACK_IMAGE;
  };

  const getStoryMediaType = (story) => {
    if (story.media && story.media.length > 0) {
      return story.media[0].type;
    }
    return story.media_type || 'image';
  };

  if (stories.length === 0) return null;

  const currentStory = activeStoryIndex !== null ? stories[activeStoryIndex] : null;
  const currentMedia = currentStory?.media?.[activeSlideIndex];
  const totalSlides = currentStory?.media?.length || 0;

  // Generate JSON-LD structured data for Google indexing
  const videoStructuredData = stories.filter(s => s.media?.some(m => m.type === 'video')).map(story => {
    const video = story.media.find(m => m.type === 'video');
    return {
      "@context": "https://schema.org",
      "@type": "VideoObject",
      "name": story.title || "Registan Tourism Story",
      "description": story.content || `Travel story by Registan Tourism - ${story.title}`,
      "thumbnailUrl": getStoryThumbnail(story),
      "uploadDate": story.createdAt || new Date().toISOString(),
      "contentUrl": video ? getMediaUrl(video.url) : "",
      "publisher": {
        "@type": "Organization",
        "name": "Registan Tourism",
        "logo": { "@type": "ImageObject", "url": COMPANY_LOGO }
      }
    };
  });

  const imageStructuredData = stories.filter(s => !s.media?.some(m => m.type === 'video')).map(story => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": story.title || "Registan Tourism Story",
    "description": story.content || `Travel story by Registan Tourism`,
    "image": getStoryThumbnail(story),
    "datePublished": story.createdAt || new Date().toISOString(),
    "author": { "@type": "Organization", "name": "Registan Tourism" },
    "publisher": {
      "@type": "Organization",
      "name": "Registan Tourism",
      "logo": { "@type": "ImageObject", "url": COMPANY_LOGO }
    }
  }));

  return (
    <>
      {/* SEO: Structured data for stories/videos */}
      {videoStructuredData.map((data, i) => (
        <script key={`video-ld-${i}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
      ))}
      {imageStructuredData.map((data, i) => (
        <script key={`article-ld-${i}`} type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
      ))}
      {/* Story Thumbnails - Instagram Style */}
      <div className="stories-container" data-testid="stories-container">
        <div className="stories-scroll">
          {stories.map((story, index) => (
            <button
              key={story.id}
              className="story-thumbnail"
              onClick={() => openStory(index)}
              data-testid={`story-thumb-${index}`}
            >
              <div className="story-ring">
                <div className="story-avatar">
                  {getStoryMediaType(story) === 'video' ? (
                    <div className="story-video-thumb">
                      <video
                        src={getStoryThumbnail(story)}
                        className="story-preview"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.classList.add('video-error');
                        }}
                      />
                      <div className="video-fallback">
                        <Play size={20} />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={getStoryThumbnail(story)}
                      alt={story.title || 'Story'}
                      className="story-preview"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,' + encodeURIComponent(`
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                            <defs>
                              <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style="stop-color:#1e3a5f"/>
                                <stop offset="50%" style="stop-color:#0d1f3c"/>
                                <stop offset="100%" style="stop-color:#0a1628"/>
                              </linearGradient>
                            </defs>
                            <rect fill="url(#grad)" width="100" height="100"/>
                            <circle cx="50" cy="40" r="15" fill="rgba(0,255,209,0.3)"/>
                            <path d="M30 70 L50 55 L70 70 L70 85 L30 85 Z" fill="rgba(0,255,209,0.2)"/>
                          </svg>
                        `.trim());
                      }}
                    />
                  )}
                  {story.media && story.media.length > 1 && (
                    <div className="story-count-badge">{story.media.length}</div>
                  )}
                </div>
              </div>
              {story.title && (
                <span className="story-title">{story.title}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Story Viewer Modal */}
      {activeStoryIndex !== null && currentStory && (
        <div className="story-viewer" data-testid="story-viewer">
          <div className="story-viewer-overlay" onClick={closeStory} />
          
          <div className="story-viewer-content">
            {/* Progress Bars - One per slide */}
            <div className="story-progress-container">
              {currentStory.media?.map((_, index) => (
                <div key={index} className="story-progress-bar">
                  <div
                    className="story-progress-fill"
                    style={{
                      width: index < activeSlideIndex ? '100%' : 
                             index === activeSlideIndex ? `${progress}%` : '0%'
                    }}
                  />
                </div>
              ))}
            </div>

            {/* Header */}
            <div className="story-viewer-header">
              <div className="story-info">
                <img src={COMPANY_LOGO} alt="Registan" className="story-viewer-avatar-img" />
                <div>
                  <span className="story-viewer-name">{currentStory.title || 'Registan Tourism'}</span>
                  <span className="story-slide-counter">{activeSlideIndex + 1}/{totalSlides}</span>
                </div>
              </div>
              <div className="story-controls">
                {currentMedia?.type === 'video' && (
                  <button onClick={toggleMute} className="story-control-btn">
                    {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </button>
                )}
                <button onClick={togglePause} className="story-control-btn">
                  {isPaused ? <Play size={20} /> : <Pause size={20} />}
                </button>
                <button onClick={closeStory} className="story-control-btn">
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Media Content */}
            <div className="story-media-container">
              {currentMedia?.type === 'video' ? (
                <video
                  ref={videoRef}
                  key={`${activeStoryIndex}-${activeSlideIndex}`}
                  src={getMediaUrl(currentMedia.url)}
                  className="story-media"
                  autoPlay
                  playsInline
                  muted={isMuted}
                  onEnded={goToNextSlide}
                />
              ) : (
                <img
                  key={`${activeStoryIndex}-${activeSlideIndex}`}
                  src={getMediaUrl(currentMedia?.url)}
                  alt={currentStory.title || 'Story'}
                  className="story-media"
                />
              )}

              {/* Navigation Areas */}
              <div className="story-nav-area story-nav-prev" onClick={goToPrevSlide}>
                {(activeSlideIndex > 0 || activeStoryIndex > 0) && (
                  <ChevronLeft size={32} className="story-nav-icon" />
                )}
              </div>
              <div className="story-nav-area story-nav-next" onClick={goToNextSlide}>
                {(activeSlideIndex < totalSlides - 1 || activeStoryIndex < stories.length - 1) && (
                  <ChevronRight size={32} className="story-nav-icon" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Stories;
